import {
  Divider,
  EHeadingStyleVariant,
  EIconButtonVariant,
  GeneralCaretLeftIcon,
  GeneralClockIcon,
  GeneralMapPinOutlineIcon,
  GeneralStarOutlineIcon,
  Heading,
  IconButton,
  Modal,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IAutocompleteOption } from '@/components/switchback/Autocomplete/AutocompleteOptions';
import useMapboxQuery from '@/hooks/useMapboxQuery';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { LocationGroupType, setSurfacedLocation } from '@/utility/surfacedLocation';

enum EListVariants {
  MATCHES = 'matches',
  RECENT_SEARCHES = 'recent_searches',
}

type TMobileSearchAddressModalContentProps = {
  onCloseHandler: () => void;
  location?: { lat: number; lng: number };
  address?: string;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  popularDestinations?: { label: string }[];
  recentSearches?: IAutocompleteOption[];
  handleRecentSearchNavigation?: (url: string) => void;
  maxRecentSearches: number;
  isDeliveryMode?: boolean;
};

export const MobileSearchAddressModalContent = ({
  location,
  onCloseHandler,
  address,
  onChangeAddress,
  popularDestinations = [],
  recentSearches = [],
  handleRecentSearchNavigation,
  maxRecentSearches,
  isDeliveryMode,
}: TMobileSearchAddressModalContentProps) => {
  const intl = useIntl();
  const [addressValue, setAddressValue] = useState(address || '');
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  // Track initial address to detect changes
  const initialAddressRef = useRef(address || '');

  useEffect(() => {
    // Update initial address when modal opens
    initialAddressRef.current = address || '';
    setAddressValue(address || '');
  }, [address]);

  const placesOption = {
    text: addressValue,
    ...(location ? { location: [location.lng, location.lat] } : {}),
    types: 'country,region,postcode,district,place,locality,neighborhood,address',
  };
  const places = useMapboxQuery(addressValue.length < 3 ? null : placesOption);
  const suggestions = places.map(p => {
    if ('name' in p) {
      // suggestion
      return {
        label: p.name_preferred || p.name,
        sublabel: p.full_address || p.place_formatted,
        value: p,
      };
    }
    // geocode
    return { label: p.place_name, value: p };
  });

  let options: IAutocompleteOption[] = [];
  if (suggestions.length) {
    options = suggestions;
  } else if (recentSearches?.length) {
    options = recentSearches?.slice(0, maxRecentSearches);
  }

  const onChangeAddressHandler = (value: string) => {
    setAddressValue(value);
    // Don't call onChangeAddress immediately - only save on selection
    setSurfacedLocation(LocationGroupType.OTHER);
  };

  // Handle back button - discard changes and close
  const handleBackButton = () => {
    // Don't save changes, just close the modal
    onCloseHandler?.();
  };

  // Handle mobile keyboard "Done" button - save changes and close
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // Save the current input value when form is submitted (mobile "Done" button)
    if (addressValue.trim()) {
      onChangeAddress?.(addressValue.trim());
    }
    onCloseHandler?.();
  };

  // Handle input blur - also triggered by mobile "Done" button in some cases
  const handleInputBlur = () => {
    // Only save if there's actual content and it's different from initial
    if (addressValue.trim() && addressValue.trim() !== initialAddressRef.current) {
      onChangeAddress?.(addressValue.trim());
    }
  };

  const onSelectPopularDestination = (value: string) => {
    setAddressValue(value);
    onChangeAddress?.(value);
    setSurfacedLocation(LocationGroupType.POPULAR_DESTINATIONS);
    onCloseHandler?.();
  };

  const onSelectLocation = (option: IAutocompleteOption) => {
    const { label, url } = option;
    if (url) {
      setAddressValue(label.split('•').map(part => part.trim())[0] || '');
      handleRecentSearchNavigation?.(url);
      return;
    }

    setSurfacedLocation(LocationGroupType.MATCHES);

    setAddressValue(label);
    onChangeAddress?.(label, option);
    onCloseHandler?.();
  };

  const getModalTitle = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return <FormattedMessage defaultMessage="Where are you heading?" id="g5vORI" />;
    }

    return isDeliveryMode ? (
      <FormattedMessage defaultMessage="Add a delivery location" id="nK3TK0" />
    ) : (
      <FormattedMessage defaultMessage="Add a pick up location" id="zHZZGe" />
    );
  };

  const getInputLabel = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({ defaultMessage: 'Address', id: 'e6Ph5+' });
    }

    return isDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Search a delivery location',
          id: 'tFvtJv',
          description: 'Location label for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Pick up location',
          id: 'BMWzhW',
          description: 'Location label for RV rental mode',
        });
  };

  return (
    <>
      <Modal.Header inlineHeader={true} className="flex items-center !p-0 gap-1">
        <IconButton
          icon={GeneralCaretLeftIcon}
          onClick={handleBackButton}
          className="text-[21px]"
          variant={EIconButtonVariant.Ghost}
        />
        <Heading variant={EHeadingStyleVariant.H6}>{getModalTitle()}</Heading>
      </Modal.Header>
      <Modal.Content className="!mt-5 !p-0">
        <div className="flex flex-col overflow-y-auto gap-4 h-[60vh]">
          <form onSubmit={handleFormSubmit}>
            <TextInput
              controlled
              label={getInputLabel()}
              inputValue={addressValue}
              onChange={onChangeAddressHandler}
              showClearButton
              onBlur={handleInputBlur}
            />
          </form>
          {suggestions && suggestions.length > 0 ? (
            <OptionsList
              options={options}
              onSelectOption={onSelectLocation}
              listVariant={EListVariants.MATCHES}
            />
          ) : (
            <>
              {recentSearches && recentSearches.length > 0 && (
                <OptionsList
                  options={options}
                  onSelectOption={onSelectLocation}
                  listVariant={EListVariants.RECENT_SEARCHES}
                />
              )}
              {recentSearches.length === 0 &&
                popularDestinations &&
                popularDestinations.length > 0 &&
                addressValue.length === 0 && (
                  <PopularDestinationsList
                    popularDestinations={popularDestinations}
                    onSelectLocation={onSelectPopularDestination}
                  />
                )}
            </>
          )}
        </div>
      </Modal.Content>
    </>
  );
};

type TPopularDestinationsListProps = {
  popularDestinations: {
    label: string;
  }[];
  onSelectLocation: (value: string) => void;
};

const PopularDestinationsList = ({
  popularDestinations,
  onSelectLocation,
}: TPopularDestinationsListProps) => {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <GeneralStarOutlineIcon />
          <Text className="font-medium">
            <FormattedMessage defaultMessage="Popular destinations" id="kp0K3N" />
          </Text>
        </div>
        <Divider />
      </div>
      <ul>
        {popularDestinations.map((des, index) => (
          <li key={des.label}>
            <button
              onClick={() => onSelectLocation(des.label)}
              className="flex flex-col items-start w-full pt-4 gap-4">
              <Text className="text-gray-50">{des.label}</Text>
              {index !== popularDestinations.length - 1 && <Divider />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

type TOptionsListProps = {
  options: IAutocompleteOption[];
  onSelectOption: (option: IAutocompleteOption) => void;
  listVariant: EListVariants;
};

const OptionsList = ({ options, onSelectOption, listVariant }: TOptionsListProps) => {
  return (
    <div className="flex flex-col">
      {listVariant === EListVariants.RECENT_SEARCHES && (
        <div className="flex flex-col mb-4 gap-4">
          <div className="flex items-center gap-2">
            <GeneralClockIcon />
            <Text className="font-medium">
              <FormattedMessage defaultMessage="Recent searches" id="h9IMab" />
            </Text>
          </div>
        </div>
      )}
      <ul>
        {options.map((option, index) => {
          const [address, ...rest] =
            listVariant === EListVariants.RECENT_SEARCHES
              ? option.label.split('•').map(part => part.trim())
              : option.label.split(',').map(part => part.trim());

          return (
            <li key={option.label}>
              <button
                onClick={() => onSelectOption(option)}
                className="flex flex-col items-start w-full pb-4 gap-4">
                {
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      <GeneralMapPinOutlineIcon />
                    </div>
                    <div className="text-start">
                      <Text className="font-medium">{address}</Text>
                      {!option.sublabel && (
                        <Text className="text-start text-gray-50">{rest.join(', ')}</Text>
                      )}
                      {!!option.sublabel && (
                        <Text className="text-start text-gray-50">{option.sublabel}</Text>
                      )}
                    </div>
                  </div>
                }
                {index !== options.length - 1 && <Divider />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
